import { ref, nextTick } from 'vue'
import { quoteEditorService } from '../../services/quoteEditorService.js'
import { calculateTotalFromPriceLines } from '../../utils/quoteUtils.js'

export function useQuoteDetailEditor(quoteId, quote, config = {}) {
  const { generatePDFPreview, showAmountModal, quoteAmountInput, isFromEditor } = config
  const showEditor = ref(false)
  const quoteEditorRef = ref(null)
  const savingEditor = ref(false)
  const generatingPDFEditor = ref(false)
  const hasZeroAmountLine = ref(false)

  const calculateAmountFromEditorData = (editorData) => {
    if (!editorData?.prix?.lignes) return null
    
    const total = calculateTotalFromPriceLines(editorData.prix.lignes)
    return total > 0 && !isNaN(total) ? total : null
  }

  const checkIfHasZeroAmountLine = (editorData) => {
    if (!editorData?.prix?.lignes || editorData.prix.lignes.length === 0) return false
    
    const total = calculateTotalFromPriceLines(editorData.prix.lignes)
    return total === 0 || isNaN(total)
  }

  const fetchEditorAmount = async () => {
    try {
      const editorData = await quoteEditorService.getEditorData(quoteId)
      return calculateAmountFromEditorData(editorData)
    } catch (e) {
      console.warn('Impossible de récupérer les données de l\'éditeur:', e)
      return null
    }
  }

  const resolveFinalAmount = async () => {
    const currentAmount = quote.value?.quote_amount
    if (currentAmount) return currentAmount
    
    if (!quoteEditorRef.value) return null
    return await fetchEditorAmount()
  }

  const showAmountModalIfNeeded = async (shouldCloseEditor = false) => {
    quoteAmountInput.value = ''
    isFromEditor.value = true
    
    // Vérifier si la première ligne est à 0€
    try {
      const editorData = await quoteEditorService.getEditorData(quoteId)
      hasZeroAmountLine.value = checkIfHasZeroAmountLine(editorData)
    } catch (e) {
      console.warn('Impossible de récupérer les données de l\'éditeur pour vérifier les lignes:', e)
      hasZeroAmountLine.value = false
    }
    
    // Fermer la modal de l'éditeur avant d'ouvrir la modal du montant
    if (shouldCloseEditor) {
      showEditor.value = false
      // Attendre que la transition de fermeture soit terminée (300ms selon le CSS)
      await nextTick()
      await new Promise(resolve => setTimeout(resolve, 350))
    }
    showAmountModal.value = true
  }

  const handleAmountError = (err, shouldCloseEditor = false) => {
    if (err.message && (err.message.includes('montant') || err.message.includes('requis'))) {
      showAmountModalIfNeeded(shouldCloseEditor)
    } else {
      console.error('Erreur lors de la génération du PDF depuis l\'éditeur:', err)
    }
  }

  const saveQuoteFromEditor = async () => {
    if (!quoteEditorRef.value) return
    
    savingEditor.value = true
    try {
      const response = await quoteEditorRef.value.saveQuote()
      // Mettre à jour le montant du quote depuis la réponse de l'API
      if (response && response.quoteAmount !== undefined && quote.value) {
        quote.value.quote_amount = response.quoteAmount
      }
      return response
    } finally {
      savingEditor.value = false
    }
  }

  const generatePDFWithAmount = async (shouldCloseEditor = false) => {
    if (!quoteEditorRef.value) return
    
    generatingPDFEditor.value = true
    let pdfGenerated = false
    
    try {
      const response = await quoteEditorRef.value.saveQuote()
      // Mettre à jour le montant du quote depuis la réponse de l'API
      if (response && response.quoteAmount !== undefined && quote.value) {
        quote.value.quote_amount = response.quoteAmount
      }
      
      const finalAmount = await resolveFinalAmount()
      
      if (!finalAmount) {
        showAmountModalIfNeeded(shouldCloseEditor)
        generatingPDFEditor.value = false
        return
      }
      
      await generatePDFPreview(finalAmount, true)
      pdfGenerated = true
      
      // Fermer la modal après génération réussie du PDF
      // Utiliser nextTick pour s'assurer que l'aperçu PDF est bien ouvert avant de fermer l'éditeur
      if (shouldCloseEditor) {
        await new Promise(resolve => setTimeout(resolve, 100))
        showEditor.value = false
      }
    } catch (err) {
      handleAmountError(err, shouldCloseEditor)
      // Si le PDF a été généré avec succès avant l'erreur, on ferme quand même la modal
      if (shouldCloseEditor && pdfGenerated) {
        showEditor.value = false
      }
    } finally {
      generatingPDFEditor.value = false
    }
  }

  const generatePDFFromEditor = async () => {
    await generatePDFWithAmount(false)
  }

  const nextFromEditor = async () => {
    await generatePDFWithAmount(true)
  }

  return {
    showEditor,
    quoteEditorRef,
    savingEditor,
    generatingPDFEditor,
    saveQuoteFromEditor,
    generatePDFFromEditor,
    nextFromEditor,
    hasZeroAmountLine
  }
}
